(() => {
  if (window.CommunityProductPicker) return;

  const debounce = (callback, delay = 250) => {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), delay);
    };
  };

  const priceNumber = (price) => {
    if (typeof price === 'number') return String(Number.isInteger(price) ? price / 100 : price);
    const numeric = String(price || '').replace(/[^\d.]/g, '');
    return numeric || '';
  };

  const priceLabel = (price, currency) => {
    if (typeof price === 'string' && /[^\d.,]/.test(price)) return price;
    const value = Number(String(price || '').replace(/,/g, ''));
    if (!Number.isFinite(value)) return '';
    const amount = Number.isInteger(value) ? value / 100 : value;
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount); } catch (error) { return `$${amount.toFixed(2)}`; }
  };

  const imageUrl = (product) => product.featured_image?.url || product.featured_image || product.image?.url || product.image || product.featured_media?.preview_image?.src || '';

  const productFromItem = (item) => {
    const choice = item.querySelector('[data-community-product-choice]');
    if (!choice) return null;
    return {
      id: choice.dataset.productId,
      title: choice.dataset.productName,
      image: choice.dataset.productImage,
      price: choice.dataset.productPrice,
      priceLabel: choice.dataset.productPriceLabel || item.querySelector('.community-product-picker__price')?.textContent.trim() || '',
      url: choice.dataset.productUrl
    };
  };

  const productFromSearch = (product, currency) => ({
    id: String(product.id || ''),
    title: product.title || '',
    image: imageUrl(product),
    price: priceNumber(product.price || product.price_min),
    priceLabel: priceLabel(product.price || product.price_min, currency),
    url: product.url || ''
  });

  const createItem = (product, selected, onChange) => {
    const item = document.createElement('label');
    item.className = 'community-product-picker__item';
    item.dataset.communityProductItem = '';
    item.dataset.searchValue = product.title.toLowerCase();

    const checkbox = document.createElement('input');
    checkbox.className = 'community-product-picker__checkbox';
    checkbox.type = 'checkbox';
    checkbox.checked = selected.has(product.id);
    checkbox.dataset.communityProductChoice = '';
    checkbox.dataset.productId = product.id;
    checkbox.dataset.productName = product.title;
    checkbox.dataset.productImage = product.image || '';
    checkbox.dataset.productPrice = product.price || '';
    checkbox.dataset.productPriceLabel = product.priceLabel || '';
    checkbox.dataset.productUrl = product.url || '';
    checkbox.addEventListener('change', (event) => onChange(event.currentTarget));

    let image;
    if (product.image) {
      image = document.createElement('img');
      image.className = 'community-product-picker__image';
      image.src = product.image;
      image.alt = product.title;
      image.width = 36;
      image.height = 36;
      image.loading = 'lazy';
    } else {
      image = document.createElement('span');
      image.className = 'community-product-picker__image community-product-picker__image--empty';
      image.setAttribute('aria-hidden', 'true');
    }

    const name = document.createElement('span');
    name.className = 'community-product-picker__name';
    name.textContent = product.title;
    const price = document.createElement('span');
    price.className = 'community-product-picker__price';
    price.textContent = product.priceLabel;
    item.append(checkbox, image, name, price);
    return item;
  };

  const init = (root) => {
    if (!root || root.dataset.communityProductPickerReady === 'true') return;
    const search = root.querySelector('[data-community-product-search]');
    const list = root.querySelector('[data-community-product-list]');
    const fields = root.querySelector('[data-community-product-fields]');
    const empty = root.querySelector('[data-community-product-empty]');
    const loading = root.querySelector('[data-community-product-loading]');
    if (!search || !list || !fields) return;
    root.dataset.communityProductPickerReady = 'true';

    const recommended = Array.from(list.querySelectorAll('[data-community-product-item]')).map(productFromItem).filter(Boolean);
    const selected = new Map();
    let displayedProducts = recommended;
    let controller;
    let requestId = 0;

    const setLoading = (isLoading) => {
      if (loading) loading.hidden = !isLoading;
      search.setAttribute('aria-busy', String(isLoading));
    };

    const writeFields = () => {
      fields.replaceChildren();
      Array.from(selected.values()).forEach((product, index) => {
        const values = { productId: product.id, productName: product.title, productImage: product.image, productPrice: product.price, productUrl: product.url };
        Object.entries(values).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `products[${index}][${key}]`;
          input.value = value || '';
          fields.appendChild(input);
        });
      });
    };

    const sync = (choice) => {
      if (choice.checked) {
        selected.set(choice.dataset.productId, {
          id: choice.dataset.productId,
          title: choice.dataset.productName,
          image: choice.dataset.productImage,
          price: choice.dataset.productPrice,
          priceLabel: choice.dataset.productPriceLabel,
          url: choice.dataset.productUrl
        });
      } else {
        selected.delete(choice.dataset.productId);
      }
      writeFields();
      render(displayedProducts);
    };

    const render = (products, message = '') => {
      displayedProducts = products;
      list.replaceChildren();
      [...products]
        .sort((first, second) => Number(selected.has(second.id)) - Number(selected.has(first.id)))
        .forEach((product) => list.appendChild(createItem(product, selected, sync)));
      if (empty) {
        empty.textContent = message || 'No matching products found.';
        empty.hidden = products.length > 0;
      }
    };

    const fetchProducts = async (query) => {
      if (controller) controller.abort();
      controller = new AbortController();
      const baseUrl = root.dataset.searchUrl || '/search/suggest';
      const url = new URL(baseUrl.endsWith('.json') ? baseUrl : `${baseUrl}.json`, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', root.dataset.searchLimit || '12');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku');
      const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Product search failed');
      const body = await response.json();
      return (body.resources?.results?.products || []).map((product) => productFromSearch(product, root.dataset.currency || 'USD')).filter((product) => product.id && product.title);
    };

    const searchSiteProducts = debounce(async () => {
      const query = search.value.trim();
      if (!query) {
        requestId += 1;
        if (controller) controller.abort();
        setLoading(false);
        const recommendedIds = new Set(recommended.map((product) => product.id));
        const selectedSearchProducts = Array.from(selected.values()).filter((product) => !recommendedIds.has(product.id));
        render([...recommended, ...selectedSearchProducts]);
        return;
      }
      const activeRequestId = ++requestId;
      setLoading(true);
      try {
        const products = await fetchProducts(query);
        if (activeRequestId !== requestId) return;
        render(products);
      } catch (error) {
        if (activeRequestId !== requestId || error.name === 'AbortError') return;
        render([], 'Unable to search products. Please try again.');
      } finally {
        if (activeRequestId === requestId) setLoading(false);
      }
    });

    list.querySelectorAll('[data-community-product-choice]:checked').forEach((choice) => sync(choice));
    list.querySelectorAll('[data-community-product-choice]').forEach((choice) => choice.addEventListener('change', (event) => sync(event.currentTarget)));
    search.addEventListener('input', searchSiteProducts);
    search.addEventListener('search', () => { if (!search.value) searchSiteProducts(); });
    writeFields();
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-community-product-picker]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  window.CommunityProductPicker = { init, initAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
})();
